import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../../../lib/supabase';
import { useVertical } from '../../../context/VerticalContext';
import {
    Landmark, Loader2, ChevronRight, CalendarClock, CreditCard, Wallet,
    ArrowDownToLine, X, CheckCircle, Info, Plus, Trash2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import ReturnsBayPage from './ReturnsBayPage';
import { errorMessage } from '../../../lib/errors';

/**
 * Supplier Accounts (Phase 1)
 *
 * Reframes supplier credits as a running account per supplier — a tab you settle
 * monthly. Reads fn_get_supplier_accounts (balance, pending credits, statement
 * day). The credit side (returns) is captured automatically; per-part PURCHASE
 * tracking and the cash/accruals P&L timing are Phase 2. The existing returns /
 * write-off / statement-upload tooling is kept below, unchanged.
 */

interface SupplierAccount {
    id: string;
    name: string;
    account_type: 'credit' | 'payg';
    statement_day: number | null;
    opening_balance: number;
    balance: number;
    credits_total: number;
    payments_total: number;
    pending_credit: number;
    last_payment_on: string | null;
    entry_count: number;
}

const gbp = (n: number) =>
    new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 2 }).format(n || 0);

// Next occurrence of a monthly statement day, from today.
function nextStatementDate(day: number | null): Date | null {
    if (!day) return null;
    const today = new Date();
    const y = today.getFullYear();
    const m = today.getMonth();
    const clamp = (yr: number, mo: number) => Math.min(day, new Date(yr, mo + 1, 0).getDate());
    let d = new Date(y, m, clamp(y, m));
    if (d < new Date(y, m, today.getDate())) {
        d = new Date(y, m + 1, clamp(y, m + 1));
    }
    return d;
}
const fmtDate = (d: Date) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
const num = (v: unknown) => (Number.isFinite(Number(v)) ? Number(v) : 0);

export default function SupplierAccountsPage() {
    const { activeOrgId } = useVertical();
    const [accounts, setAccounts] = useState<SupplierAccount[]>([]);
    const [loading, setLoading] = useState(true);
    const [openId, setOpenId] = useState<string | null>(null);
    const [editing, setEditing] = useState<SupplierAccount | null>(null);
    const [adding, setAdding] = useState(false);
    const [paying, setPaying] = useState<SupplierAccount | null>(null);
    const [deleting, setDeleting] = useState<SupplierAccount | null>(null);

    const fetchAccounts = useCallback(async () => {
        if (!activeOrgId) return;
        setLoading(true);
        try {
            const { data, error } = await supabase.rpc('fn_get_supplier_accounts', { p_org_id: activeOrgId });
            if (error) throw error;
            const rows = ((data ?? []) as any[]).map((r) => ({
                ...r,
                opening_balance: num(r.opening_balance),
                balance: num(r.balance),
                credits_total: num(r.credits_total),
                payments_total: num(r.payments_total),
                pending_credit: num(r.pending_credit),
                entry_count: num(r.entry_count),
            })) as SupplierAccount[];
            setAccounts(rows);
        } catch (err) {
            console.error('Failed to load supplier accounts:', err);
            setAccounts([]);
        } finally {
            setLoading(false);
        }
    }, [activeOrgId]);

    useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

    const creditAccounts = useMemo(() => accounts.filter((a) => a.account_type === 'credit'), [accounts]);

    const totals = useMemo(() => {
        const owed = creditAccounts.reduce((s, a) => s + Math.max(a.balance, 0), 0);
        const pending = accounts.reduce((s, a) => s + a.pending_credit, 0);
        const upcoming = creditAccounts
            .map((a) => ({ a, d: nextStatementDate(a.statement_day) }))
            .filter((x) => x.d && a_hasBalance(x.a))
            .sort((x, y) => (x.d!.getTime() - y.d!.getTime()))[0];
        return { owed, pending, upcoming };
    }, [accounts, creditAccounts]);

    function a_hasBalance(a: SupplierAccount) { return a.balance > 0.005; }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-accent/15 flex items-center justify-center shrink-0">
                    <Landmark className="w-5 h-5 text-accent" />
                </div>
                <div>
                    <h1 className="text-2xl font-bold text-fg">Supplier Accounts</h1>
                    <p className="text-sm text-fg-3 max-w-2xl mt-0.5">
                        Every supplier you buy from on account is a running account — like a tab you settle monthly.
                        Parts push it up, returns bring it down. <span className="text-fg">ForgeDash keeps the books straight and sorted for you.</span>
                    </p>
                </div>
            </div>

            {/* Summary */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <SummaryTile
                    icon={<Wallet className="w-4 h-4" />}
                    label="Total across your accounts"
                    value={gbp(totals.owed)}
                    sub={`${creditAccounts.length} credit account${creditAccounts.length === 1 ? '' : 's'}`}
                    tone="owe"
                    loading={loading}
                />
                <SummaryTile
                    icon={<ArrowDownToLine className="w-4 h-4" />}
                    label="Returns awaiting credit"
                    value={gbp(totals.pending)}
                    sub="confirm on next statement"
                    tone="credit"
                    loading={loading}
                />
                <SummaryTile
                    icon={<CalendarClock className="w-4 h-4" />}
                    label="Next statement due"
                    value={totals.upcoming?.d ? totals.upcoming.a.name : '—'}
                    sub={totals.upcoming?.d ? fmtDate(totals.upcoming.d) : 'no dates set'}
                    tone="due"
                    small
                    loading={loading}
                />
                <SummaryTile
                    icon={<Info className="w-4 h-4" />}
                    label="Held back from Safe-to-Spend"
                    value={gbp(totals.owed)}
                    sub="the full balance you owe"
                    tone="reserve"
                    loading={loading}
                />
            </div>

            {/* Accounts list */}
            <div>
                <div className="flex items-center justify-between gap-3 mb-3">
                    <div className="min-w-0">
                        <h2 className="text-xs font-bold uppercase tracking-[0.14em] text-fg-3">Your accounts</h2>
                        <span className="text-xs text-fg-4 hidden sm:inline">Tap an account to see the detail and record a payment.</span>
                    </div>
                    <button
                        onClick={() => setAdding(true)}
                        className="shrink-0 inline-flex items-center gap-2 px-3.5 py-2 rounded-lg fd-btn-primary font-bold text-sm transition-all"
                    >
                        <Plus className="w-4 h-4" /> Add account
                    </button>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-16">
                        <Loader2 className="w-6 h-6 animate-spin text-accent" />
                    </div>
                ) : accounts.length === 0 ? (
                    <div className="text-center py-14 bg-raised rounded-xl border border-tint/5">
                        <Landmark className="w-10 h-10 mx-auto text-fg-4 mb-3" />
                        <p className="text-fg-3 font-medium">No supplier accounts yet</p>
                        <p className="text-fg-4 text-sm mt-1 max-w-md mx-auto">
                            Add a supplier you buy from on account, or flag a part for return from a job, and it shows up here.
                        </p>
                        <button
                            onClick={() => setAdding(true)}
                            className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg fd-btn-primary font-bold text-sm transition-all"
                        >
                            <Plus className="w-4 h-4" /> Add your first account
                        </button>
                    </div>
                ) : (
                    <div className="flex flex-col gap-3">
                        {accounts.map((a) => {
                            const open = openId === a.id;
                            const due = nextStatementDate(a.statement_day);
                            const settled = !a_hasBalance(a);
                            return (
                                <div key={a.id} className="bg-panel border border-line rounded-xl overflow-hidden">
                                    <button
                                        onClick={() => setOpenId(open ? null : a.id)}
                                        aria-expanded={open}
                                        className="w-full text-left px-5 py-4 flex items-center gap-4 hover:bg-tint/[0.02] transition-colors"
                                    >
                                        <ChevronRight className={`w-4 h-4 shrink-0 transition-transform ${open ? 'rotate-90 text-accent' : 'text-fg-4'}`} />
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="font-bold text-fg">{a.name}</span>
                                                {a.account_type === 'credit' ? (
                                                    <span className="text-fd-sm-tight font-bold px-2 py-0.5 rounded-full border border-accent/40 bg-accent/10 text-accent">Credit account</span>
                                                ) : (
                                                    <span className="text-fd-sm-tight font-bold px-2 py-0.5 rounded-full border border-tint/15 bg-tint/5 text-fg-2">Pay-as-you-go</span>
                                                )}
                                            </div>
                                            <div className="text-xs text-fg-4 mt-1">
                                                {a.account_type === 'credit'
                                                    ? (a.statement_day ? `Billed monthly · statement ~${ordinal(a.statement_day)}${due ? ` · due ${fmtDate(due)}` : ''}` : 'Billed monthly · statement day not set')
                                                    : 'Paid at the till · no account to settle'}
                                                {a.pending_credit > 0.005 && (
                                                    <span className="ml-2 text-gold">· {gbp(a.pending_credit)} return credit pending</span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="text-right shrink-0">
                                            <div className="text-fd-3xs uppercase tracking-wider text-fg-4">{a.account_type === 'credit' ? 'Balance' : 'On the books'}</div>
                                            <div className={`text-2xl font-bold font-mono tabular-nums ${settled ? 'text-success' : 'text-gold'}`}>
                                                {settled && a.account_type === 'credit' ? '£0' : gbp(a.balance)}
                                            </div>
                                            {settled && a.account_type === 'credit' && <div className="text-xs text-fg-4">All square</div>}
                                        </div>
                                    </button>

                                    {open && (
                                        <div className="border-t border-dashed border-tint/10 px-5 py-4 space-y-4 animate-in fade-in">
                                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                                <Stat label="Opening balance" value={gbp(a.opening_balance)} />
                                                <Stat label="Returns credited" value={`− ${gbp(a.credits_total)}`} good />
                                                <Stat label="Payments made" value={`− ${gbp(a.payments_total)}`} good />
                                                <Stat label="Awaiting credit" value={gbp(a.pending_credit)} />
                                            </div>

                                            <p className="text-fd-sm-tight text-fg-4 flex items-start gap-1.5">
                                                <Info className="w-3.5 h-3.5 shrink-0 mt-px" />
                                                Returns are captured automatically. Purchases from your jobs will add here once per-part supplier tracking is switched on — until then, set the opening balance to what you owe today.
                                            </p>

                                            <div className="flex flex-wrap gap-2">
                                                {a.account_type === 'credit' && (
                                                    <button
                                                        onClick={() => setPaying(a)}
                                                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg fd-btn-primary font-bold text-sm transition-all"
                                                    >
                                                        <CreditCard className="w-4 h-4" /> Record statement payment
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => setEditing(a)}
                                                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-tint/5 border border-tint/10 text-fg font-semibold text-sm hover:bg-tint/10 transition-colors"
                                                >
                                                    Set up account
                                                </button>
                                                <button
                                                    onClick={() => setDeleting(a)}
                                                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-tint/5 border border-tint/10 text-fg-3 font-semibold text-sm hover:bg-danger/10 hover:border-danger/30 hover:text-danger transition-colors ml-auto"
                                                >
                                                    <Trash2 className="w-4 h-4" /> Remove
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Existing returns / write-offs / statement tooling, kept intact */}
            <div className="pt-4 border-t border-tint/5">
                <h2 className="text-xs font-bold uppercase tracking-[0.14em] text-fg-3 mb-1">Returns, write-offs &amp; statements</h2>
                <p className="text-xs text-fg-4 mb-4">Flag returns, upload supplier statements and credit notes, and review the running credit history.</p>
                <ReturnsBayPage />
            </div>

            {adding && (
                <AccountSetupModal
                    account={null}
                    orgId={activeOrgId!}
                    existingNames={accounts.map((a) => a.name)}
                    onClose={() => setAdding(false)}
                    onSaved={() => { setAdding(false); fetchAccounts(); }}
                />
            )}
            {editing && (
                <AccountSetupModal
                    account={editing}
                    orgId={activeOrgId!}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); fetchAccounts(); }}
                />
            )}
            {deleting && (
                <DeleteAccountModal
                    account={deleting}
                    orgId={activeOrgId!}
                    onClose={() => setDeleting(null)}
                    onDeleted={() => { setDeleting(null); setOpenId(null); fetchAccounts(); }}
                />
            )}
            {paying && (
                <RecordPaymentModal
                    account={paying}
                    orgId={activeOrgId!}
                    onClose={() => setPaying(null)}
                    onSaved={() => { setPaying(null); fetchAccounts(); }}
                />
            )}
        </div>
    );
}

function ordinal(n: number) {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

function SummaryTile({ icon, label, value, sub, tone, small, loading }: {
    icon: React.ReactNode; label: string; value: string; sub: string;
    tone: 'owe' | 'credit' | 'due' | 'reserve'; small?: boolean; loading?: boolean;
}) {
    const color = tone === 'owe' ? 'text-gold' : tone === 'credit' ? 'text-success' : 'text-fg';
    return (
        <div className="bg-raised border border-tint/5 rounded-xl p-4">
            <div className="flex items-center gap-2 text-fg-4">
                {icon}
                <p className="text-fd-sm-tight font-bold uppercase tracking-wider">{label}</p>
            </div>
            {loading ? (
                <div className="mt-2 h-7 w-24 rounded bg-tint/5 animate-pulse" />
            ) : (
                <p className={`font-bold mt-2 ${small ? 'text-lg' : 'text-2xl'} ${color} ${small ? '' : 'font-mono tabular-nums'}`}>{value}</p>
            )}
            <p className="text-xs text-fg-4 mt-1">{sub}</p>
        </div>
    );
}

function Stat({ label, value, good }: { label: string; value: string; good?: boolean }) {
    return (
        <div className="bg-tint/[0.02] border border-tint/5 rounded-lg px-3 py-2">
            <div className="text-fd-3xs uppercase tracking-wider text-fg-4">{label}</div>
            <div className={`text-sm font-mono font-bold mt-0.5 ${good ? 'text-success' : 'text-fg'}`}>{value}</div>
        </div>
    );
}

function AccountSetupModal({ account, orgId, existingNames, onClose, onSaved }: {
    account: SupplierAccount | null; orgId: string; existingNames?: string[];
    onClose: () => void; onSaved: () => void;
}) {
    const isNew = account == null;
    const [name, setName] = useState<string>(account?.name ?? '');
    const [type, setType] = useState<'credit' | 'payg'>(account?.account_type ?? 'credit');
    const [opening, setOpening] = useState<string>(account?.opening_balance ? String(account.opening_balance) : '');
    const [day, setDay] = useState<string>(account?.statement_day ? String(account.statement_day) : '');
    const [busy, setBusy] = useState(false);

    const trimmedName = name.trim();
    const dupExisting = isNew && existingNames?.some((n) => n.trim().toLowerCase() === trimmedName.toLowerCase());

    const save = async () => {
        setBusy(true);
        try {
            const finalName = isNew ? trimmedName : account!.name;
            if (isNew && finalName === '') throw new Error('Enter a supplier name');
            const openingNum = opening.trim() === '' ? null : Number(opening);
            const dayNum = day.trim() === '' ? null : parseInt(day, 10);
            if (openingNum != null && !Number.isFinite(openingNum)) throw new Error('Enter a valid opening balance');
            if (dayNum != null && (dayNum < 1 || dayNum > 31)) throw new Error('Statement day must be between 1 and 31');
            const { error } = await supabase.rpc('fn_upsert_supplier_account', {
                p_org_id: orgId,
                p_name: finalName,
                p_account_type: type,
                // For a PAYG account there's no balance to carry; default opening to 0 on create.
                p_opening_balance: type === 'credit' ? openingNum : (isNew ? 0 : openingNum),
                p_statement_day: type === 'credit' ? dayNum : null,
            });
            if (error) throw error;
            toast.success(isNew ? 'Account added' : 'Account saved');
            onSaved();
        } catch (err) {
            toast.error(errorMessage(err) || 'Could not save account');
        } finally {
            setBusy(false);
        }
    };

    return (
        <ModalShell title={isNew ? 'Add supplier account' : `Set up ${account!.name}`} onClose={onClose}>
            {isNew && (
                <Field label="Supplier name">
                    <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="e.g. GSF Car Parts"
                        className={inputCls}
                        autoFocus
                    />
                    {dupExisting && (
                        <p className="text-fd-sm-tight text-gold mt-1.5">You already have an account with this name — saving will update it.</p>
                    )}
                </Field>
            )}
            <label className="block">
                <span className="text-xs font-bold uppercase tracking-wider text-fg-3">Account type</span>
                <div className="flex gap-2 mt-2">
                    <TypeBtn active={type === 'credit'} onClick={() => setType('credit')} label="Credit account" hint="Billed monthly" />
                    <TypeBtn active={type === 'payg'} onClick={() => setType('payg')} label="Pay-as-you-go" hint="Paid at the till" />
                </div>
            </label>
            {type === 'credit' && (
                <>
                    <Field label="What you owe them today (opening balance)">
                        <div className="flex items-center gap-2">
                            <span className="text-fg-4">£</span>
                            <input type="number" step="0.01" min="0" value={opening} onChange={(e) => setOpening(e.target.value)}
                                placeholder="0.00" className={inputCls} />
                        </div>
                    </Field>
                    <Field label="Statement day of month (optional — we can learn it)">
                        <input type="number" min="1" max="31" value={day} onChange={(e) => setDay(e.target.value)}
                            placeholder="e.g. 28" className={inputCls} />
                    </Field>
                </>
            )}
            <div className="flex justify-end gap-2 pt-2">
                <button onClick={onClose} disabled={busy} className="px-4 py-2 rounded-lg bg-tint/5 border border-tint/10 text-fg-2 text-sm font-semibold hover:bg-tint/10">Cancel</button>
                <button onClick={save} disabled={busy || (isNew && trimmedName === '')} className="px-4 py-2 rounded-lg fd-btn-primary text-sm font-bold inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />} {isNew ? 'Add account' : 'Save'}
                </button>
            </div>
        </ModalShell>
    );
}

function DeleteAccountModal({ account, orgId, onClose, onDeleted }: {
    account: SupplierAccount; orgId: string; onClose: () => void; onDeleted: () => void;
}) {
    const [busy, setBusy] = useState(false);
    const hasBalance = account.balance > 0.005;

    const remove = async () => {
        setBusy(true);
        try {
            const { error } = await supabase.rpc('fn_delete_supplier_account', {
                p_org_id: orgId,
                p_supplier_account_id: account.id,
            });
            if (error) throw error;
            toast.success(`${account.name} removed`);
            onDeleted();
        } catch (err) {
            toast.error(errorMessage(err) || 'Could not remove account');
        } finally {
            setBusy(false);
        }
    };

    return (
        <ModalShell title={`Remove ${account.name}?`} onClose={onClose}>
            <p className="text-sm text-fg-3">
                This hides <span className="text-fg font-semibold">{account.name}</span> from your accounts. Its return and payment history is kept — if this supplier comes up again (a new return, or you add them back), the account reappears with everything intact.
            </p>
            {hasBalance && (
                <div className="flex items-start gap-2 rounded-lg bg-gold-fill/10 border border-gold/25 px-3 py-2.5">
                    <Info className="w-4 h-4 text-gold shrink-0 mt-0.5" />
                    <p className="text-xs text-gold">
                        This account still shows a balance of <span className="font-bold font-mono">{gbp(account.balance)}</span>. Removing it won't clear what you owe the supplier — record the statement payment first if it's settled.
                    </p>
                </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
                <button onClick={onClose} disabled={busy} className="px-4 py-2 rounded-lg bg-tint/5 border border-tint/10 text-fg-2 text-sm font-semibold hover:bg-tint/10">Cancel</button>
                <button onClick={remove} disabled={busy} className="px-4 py-2 rounded-lg bg-danger/90 text-on-fill text-sm font-bold hover:bg-danger-fill inline-flex items-center gap-2 disabled:opacity-50">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Remove account
                </button>
            </div>
        </ModalShell>
    );
}

function RecordPaymentModal({ account, orgId, onClose, onSaved }: {
    account: SupplierAccount; orgId: string; onClose: () => void; onSaved: () => void;
}) {
    const [amount, setAmount] = useState<string>(account.balance > 0 ? account.balance.toFixed(2) : '');
    const [paidOn, setPaidOn] = useState<string>(new Date().toISOString().slice(0, 10));
    const [busy, setBusy] = useState(false);

    const save = async () => {
        setBusy(true);
        try {
            const amt = Number(amount);
            if (!Number.isFinite(amt) || amt <= 0) throw new Error('Enter a valid amount');
            const { error } = await supabase.rpc('fn_record_supplier_payment', {
                p_org_id: orgId,
                p_supplier_account_id: account.id,
                p_amount: amt,
                p_paid_on: paidOn || null,
                p_note: null,
            });
            if (error) throw error;
            toast.success(`Payment of ${gbp(amt)} recorded for ${account.name}`);
            onSaved();
        } catch (err) {
            toast.error(errorMessage(err) || 'Could not record payment');
        } finally {
            setBusy(false);
        }
    };

    return (
        <ModalShell title={`Pay ${account.name}`} onClose={onClose}>
            <p className="text-sm text-fg-3">Recording a statement payment reduces this account's balance.</p>
            <Field label="Amount paid">
                <div className="flex items-center gap-2">
                    <span className="text-fg-4">£</span>
                    <input type="number" step="0.01" min="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className={inputCls} autoFocus />
                </div>
            </Field>
            <Field label="Paid on">
                <input type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} className={`${inputCls} [color-scheme:dark]`} />
            </Field>
            <div className="flex justify-end gap-2 pt-2">
                <button onClick={onClose} disabled={busy} className="px-4 py-2 rounded-lg bg-tint/5 border border-tint/10 text-fg-2 text-sm font-semibold hover:bg-tint/10">Cancel</button>
                <button onClick={save} disabled={busy} className="px-4 py-2 rounded-lg fd-btn-primary text-sm font-bold inline-flex items-center gap-2">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />} Record payment
                </button>
            </div>
        </ModalShell>
    );
}

function ModalShell({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-scrim/80 backdrop-blur-sm">
            <div className="w-full max-w-md bg-panel border border-line rounded-2xl shadow-2xl p-6 space-y-4 animate-in zoom-in-95">
                <div className="flex items-center justify-between">
                    <h3 className="text-lg font-bold text-fg">{title}</h3>
                    <button onClick={onClose} className="p-2 rounded-lg text-fg-3 hover:text-fg hover:bg-tint/10"><X className="w-5 h-5" /></button>
                </div>
                {children}
            </div>
        </div>
    );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <label className="block">
            <span className="text-xs font-bold uppercase tracking-wider text-fg-3 block mb-1.5">{label}</span>
            {children}
        </label>
    );
}

function TypeBtn({ active, onClick, label, hint }: { active: boolean; onClick: () => void; label: string; hint: string }) {
    return (
        <button type="button" onClick={onClick}
            className={`flex-1 rounded-lg border px-3 py-2 text-left transition-colors ${active ? 'border-accent bg-accent/10' : 'border-tint/10 bg-tint/5 hover:bg-tint/10'}`}>
            <div className={`text-sm font-bold ${active ? 'text-accent' : 'text-fg'}`}>{label}</div>
            <div className="text-fd-sm-tight text-fg-4">{hint}</div>
        </button>
    );
}

const inputCls = 'w-full bg-tint/5 border border-tint/10 rounded-lg px-3 py-2.5 text-fg text-sm outline-none focus:border-accent/60 transition-colors';
